import { useState, useEffect, useCallback } from 'react';

import { useDataContext } from '../context/DataContext.tsx';
import { useSurfaceContext } from '../context/SurfaceContext.tsx';
import {
	getBindingCandidatePaths,
	resolveBindingValue,
	resolveDynamicValue,
	type ResolverContext,
} from '../engine/dynamicResolver.ts';
import type { DynamicValue } from '../types/A2UICommon.types.tsx';
import { isDataBinding, isFunctionCall } from '../types/A2UICommon.types.tsx';

export function useDynamicValue<T = unknown>(value: DynamicValue | undefined): T | undefined {
	const { state, customFunctions } = useSurfaceContext();
	const { basePath } = useDataContext();
	const { dataModel } = state;

	const getResolverContext = useCallback(
		(): ResolverContext => ({
			dataModel,
			basePath,
			customFunctions,
		}),
		[dataModel, basePath, customFunctions],
	);

	const [resolved, setResolved] = useState<unknown>(() => {
		if (value === undefined) {
			return undefined;
		}
		return resolveDynamicValue(value, getResolverContext());
	});

	useEffect(() => {
		if (value === undefined) {
			setResolved(undefined);
			return;
		}

		// For literals, just set once
		if (
			typeof value === 'string' ||
			typeof value === 'number' ||
			typeof value === 'boolean' ||
			Array.isArray(value)
		) {
			setResolved(value);
			return;
		}

		// For data bindings, subscribe to path changes
		if (isDataBinding(value)) {
			const candidatePaths = getBindingCandidatePaths(basePath, value.path);
			setResolved(resolveBindingValue(dataModel, basePath, value.path));

			const unsubscribes = candidatePaths.map((candidatePath) =>
				dataModel.subscribe(candidatePath, () => {
					setResolved(resolveBindingValue(dataModel, basePath, value.path));
				}),
			);

			return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
		}

		// For function calls, resolve and subscribe to all binding args
		if (isFunctionCall(value)) {
			const resolve = () => resolveDynamicValue(value, getResolverContext());
			setResolved(resolve());

			// Subscribe to any data bindings in args
			const unsubscribes: (() => void)[] = [];
			for (const argValue of Object.values(value.args)) {
				if (isDataBinding(argValue)) {
					const candidatePaths = getBindingCandidatePaths(basePath, argValue.path);
					for (const candidatePath of candidatePaths) {
						unsubscribes.push(
							dataModel.subscribe(candidatePath, () => {
								setResolved(resolve());
							}),
						);
					}
				}
			}
			if (unsubscribes.length > 0) {
				return () => unsubscribes.forEach((unsub) => unsub());
			}
		}
	}, [value, dataModel, basePath, getResolverContext]);

	return resolved as T | undefined;
}

export function useDynamicString(value: DynamicValue | undefined, fallback = ''): string {
	const resolved = useDynamicValue<string>(value);
	return resolved != null ? String(resolved) : fallback;
}

export function useDynamicNumber(value: DynamicValue | undefined, fallback = 0): number {
	const resolved = useDynamicValue<number>(value);
	return typeof resolved === 'number' ? resolved : Number(resolved) || fallback;
}

export function useDynamicBoolean(value: DynamicValue | undefined, fallback = false): boolean {
	const resolved = useDynamicValue<boolean>(value);
	return resolved != null ? Boolean(resolved) : fallback;
}
